import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Chip,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import api from '../services/api';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import clienteService from '../services/clienteService';

interface Cliente {
  nroCliente: number;
  nombre: string;
  razonSocial?: string;
  cuit?: string;
  direccion?: string;
  telefono1?: string;
  email?: string;
  tipoIva?: string;
  vendedor?: number;
}

interface ClientePagedResult {
  items: Cliente[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const Clientes: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [clientesData, setClientesData] = useState<ClientePagedResult>({
    items: [], totalCount: 0, pageNumber: 1, pageSize: 10, totalPages: 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [openImportar, setOpenImportar] = useState(false);

  const fetchClientes = async (pageNumber: number, pageSize: number, search?: string) => {
    const rawSearch = (search ?? '').trim();
    const compact = rawSearch.replace(/\s+/g, '');
    const isNumericSearch = compact !== '' && /^\d+$/.test(compact);
    if (isNumericSearch) {
      try {
        const nro = Number(compact);
        const cliente = await clienteService.getCliente(nro);
        const adjustedData = {
          items: cliente ? [cliente] : [],
          totalCount: cliente ? 1 : 0,
          pageNumber: 0,
          pageSize,
          totalPages: 1
        };
        setClientesData(adjustedData);
        return;
      } catch (error: any) {
        if (error?.response?.status === 404) {
          setClientesData({ items: [], totalCount: 0, pageNumber: 0, pageSize, totalPages: 0 });
          return;
        }
        console.error('Error al buscar por nroCliente:', error);
        setClientesData({ items: [], totalCount: 0, pageNumber: 0, pageSize, totalPages: 0 });
        return;
      }
    }
    try {
      const response = await api.get('/api/clientes', {
        params: { pageNumber: pageNumber + 1, pageSize, searchTerm: rawSearch }
      });
      const adjustedData = {
        ...response.data,
        pageNumber: response.data.pageNumber - 1
      };
      setClientesData(adjustedData);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchClientes(page, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };
  const handleDelete = async (nroCliente: number) => {
    if (window.confirm('¿Está seguro de eliminar este cliente?')) {
      try {
        await api.delete(`/api/clientes/${nroCliente}`);
        fetchClientes(page, rowsPerPage, searchTerm);
      } catch (error) {
        console.error('Error al eliminar cliente:', error);
      }
    }
  };

  const handleDescargarPlantilla = () => {
    try {
      const headers = [
        'nroCliente', 'nombre', 'razonSocial', 'direccion', 'codigoCiudad', 'telefono1', 'telefono2', 'email', 'cuit', 'tipoIva', 'condVta', 'credito', 'nroLista', 'contacto', 'fechaAlta', 'fechaBaja', 'tipoCliente', 'ctaMaestra', 'deposito', 'nroClienteExterno', 'vendedor',
        'porcDesc', 'porcDesc2', 'porcDesc3' // agregado para nueva API
      ];
      const data = [headers, new Array(headers.length).fill('')];
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Clientes');
      const fileName = 'PlantillaClientes.xlsx';
      XLSX.writeFile(wb, fileName);
      enqueueSnackbar('✅ Plantilla Excel descargada correctamente', { variant: 'success', autoHideDuration: 4000 });
    } catch (error) {
      enqueueSnackbar('❌ Error al crear la plantilla Excel', { variant: 'error', autoHideDuration: 4000 });
    }
  };

  const getTipoIvaChip = (tipoIva?: string) => {
    if (!tipoIva) {
      return {
        label: 'Sin dato',
        bgColor: '#9e9e9e',
        textColor: '#ffffff'
      };
    }

    const normalized = tipoIva.toUpperCase();

    if (normalized === 'I' || normalized.includes('RESPONSABLE')) {
      return {
        label: 'Resp. Insc.',
        bgColor: '#1976d2',
        textColor: '#ffffff'
      };
    }

    if (normalized === 'M' || normalized.includes('MONOTRIBUTO')) {
      return {
        label: 'Monotributo',
        bgColor: '#4caf50',
        textColor: '#ffffff'
      };
    }

    if (normalized === 'E' || normalized.includes('EXENTO')) {
      return {
        label: 'Exento',
        bgColor: '#ff9800',
        textColor: '#ffffff'
      };
    }

    if (normalized === 'C' || normalized.includes('CONSUMIDOR')) {
      return {
        label: 'Cons. Final',
        bgColor: '#9c27b0',
        textColor: '#ffffff'
      };
    }

    return {
      label: tipoIva,
      bgColor: '#757575',
      textColor: '#ffffff'
    };
  };


  const onImportChangeClientes = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      // Leer el archivo Excel
      const fileBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(fileBuffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '', header: 0 });
      console.log("Cabeceras detectadas por XLSX:", Object.keys(rows[0]));
      enqueueSnackbar('Archivo leído, procesando filas.', { variant: 'info', autoHideDuration: 3000 });
      // Definir campos requeridos y lista completa de campos aceptados (forma canónica para la API)
      const camposRequeridosApi = ['nroCliente', 'nombre', 'email'];
      const camposApi = [
        'nroCliente', 'nombre', 'razonSocial', 'direccion', 'codigoCiudad', 'telefono1', 'telefono2', 'email', 'cuit', 'tipoIva', 'condVta', 'credito', 'nroLista', 'contacto', 'fechaAlta', 'fechaBaja', 'tipoCliente', 'ctaMaestra', 'deposito', 'nroClienteExterno', 'vendedor', 'porcDesc', 'porcDesc2', 'porcDesc3'
      ];
      const camposNumericosApi = ['nroCliente','codigoCiudad','credito','deposito','ctaMaestra','vendedor','porcDesc','porcDesc2','porcDesc3'];
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      if (!rows.length) {
        enqueueSnackbar('El archivo está vacío.', { variant: 'warning', autoHideDuration: 4000 });
        return;
      }
      const headers = Object.keys(rows[0]);
      const headersLower = headers.map(h => String(h).toLowerCase().trim());
      const noEncontrados = camposApi.filter(h => !headersLower.includes(h.toLowerCase()));
      if (noEncontrados.length > 0) {
        enqueueSnackbar('Columnas faltantes: ' + noEncontrados.join(', '), { variant: 'error', autoHideDuration: 5000 });
        return;
      }
      // Obtener todos los clientes (cargar en lotes de 20)
      const pageSize = 20; // Límite del backend
      const clientesResponse = await clienteService.getClientes({ pageNumber: 1, pageSize });
      // const todosClientes = clientesResponse.items ?? []; // No se usa actualmente
      let exitos = 0;
      let fallos = 0;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const rowNormalizado: Record<string, any> = {};
        Object.keys(row).forEach((key) => {
          rowNormalizado[key.toLowerCase().trim()] = row[key];
        });
        console.log(`Fila completa fila ${i+2}:`, rowNormalizado);

        let valido = true;
        for (const campo of camposRequeridosApi) {
          const lower = campo.toLowerCase();
          let valor = rowNormalizado[lower];
          if (campo === 'nroCliente') {
            if (typeof valor === 'string') {
              valor = valor.replace(/[,]/g, '.').replace(/ /g, '');
            }
            console.log(`Valor real nroCliente fila ${i+2}:`, valor, typeof valor, 'Número convertido:', Number(valor));
          }
          if (
            valor === undefined || valor === null || String(valor) === '' ||
            (typeof valor === 'string' && valor.trim().toLowerCase() === 'string') ||
            (campo === 'nroCliente' && (isNaN(Number(valor)) || Number(valor) === 0))
          ) {
            enqueueSnackbar(`❌ Fila ${i+2}: El campo obligatorio "${campo}" está vacío o mal completado.`, {variant:'error', autoHideDuration:8000});
            fallos++;
            valido = false;
            break;
          }
        }
        if (!valido) continue;

        // Validación de tipo numérico para columnas numéricas (si traen contenido no numérico)
        for (const campoNum of camposNumericosApi) {
          const lower = campoNum.toLowerCase();
          let raw = rowNormalizado[lower];
          if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
            if (typeof raw === 'string') {
              raw = raw.replace(/[,]/g, '.').replace(/ /g, '');
            }
            if (isNaN(Number(raw))) {
              enqueueSnackbar(`❌ Fila ${i+2}: La columna "${campoNum}" debe ser numérica.`, { variant: 'error', autoHideDuration: 9000 });
              fallos++;
              valido = false;
              break;
            }
          }
        }
        if (!valido) continue;

        // Validación de formato de email
        const rawEmail = rowNormalizado['email'];
        const emailStr = String(rawEmail ?? '').trim();
        if (!emailRegex.test(emailStr)) {
          enqueueSnackbar(`❌ Fila ${i+2}: El email "${emailStr}" tiene formato inválido. Escriba un correo válido (ej: nombre@dominio.com).`, { variant: 'error', autoHideDuration: 9000 });
          fallos++;
          continue;
        }

        // Crear cliente SOLO si pasó la validación
        const cliente: any = {};
        camposApi.forEach((key) => {
          const lower = key.toLowerCase();
          let valor = rowNormalizado[lower];
          if (key === 'nroCliente' && typeof valor === 'string') {
            valor = valor.replace(/[,]/g, '.').replace(/ /g, '');
            cliente[key] = valor === '' ? null : Number(valor);
          } else if (camposNumericosApi.includes(key)) {
            cliente[key] = (valor !== undefined && valor !== null && String(valor).trim() !== '' && !isNaN(Number(valor))) ? Number(valor) : null;
          } else if (key === 'cuit') {
            cliente[key] = (valor !== undefined && valor !== null && valor !== '' ? String(valor) : null);
          } else if (["fechaAlta","fechaBaja"].includes(key)) {
            cliente[key] = (typeof valor === 'string' && valor.match(/^\d{4}-\d{2}-\d{2}/)) ? (new Date(valor + 'T00:00:00Z')).toISOString() : (valor ? (isNaN(new Date(valor).getTime()) ? null : new Date(valor).toISOString()) : null);
          } else if (key === 'email') {
            cliente[key] = (valor === undefined || valor === '') ? null : String(valor).trim().toLowerCase();
          } else {
            cliente[key] = valor === undefined || valor === '' ? null : valor;
          }
        });

        // Validar nombre específico de forma extra-stricta
        if (
          cliente.nombre === undefined || cliente.nombre === null ||
          typeof cliente.nombre !== 'string' ||
          cliente.nombre.trim() === '' ||
          cliente.nombre.trim().toLowerCase() === 'string'
        ) {
          enqueueSnackbar(`❌ Fila ${i+2}: El campo obligatorio "nombre" está vacío, mal completado o tiene texto "string".`, {variant:'error', autoHideDuration:9000});
          fallos++;
          continue;
        }

        try {
          // Asegurar que campos numéricos inválidos NO se envíen como string
          if (cliente.deposito === '' || cliente.deposito === undefined || cliente.deposito === null || isNaN(Number(cliente.deposito))) cliente.deposito = 0;
          if (cliente.ctaMaestra !== null && isNaN(Number(cliente.ctaMaestra))) cliente.ctaMaestra = null;
          if (cliente.vendedor !== null && isNaN(Number(cliente.vendedor))) cliente.vendedor = null;
          if (cliente.credito !== null && isNaN(Number(cliente.credito))) cliente.credito = null;
          if (cliente.codigoCiudad !== null && isNaN(Number(cliente.codigoCiudad))) cliente.codigoCiudad = null;

          // Enviar objeto plano camelCase exactamente como en el Excel/backend
          const clientePayload: any = { ...cliente };
          // Tipos numéricos saneados
          clientePayload.deposito = Number(clientePayload.deposito ?? 0);
          clientePayload.nroCliente = Number(clientePayload.nroCliente);
          console.log('📦 Payload cliente (API):', clientePayload);
          await clienteService.createCliente(clientePayload as any);
          exitos++;
          await fetchClientes(page, rowsPerPage, searchTerm);
        } catch (err: any) {
          let mensajePlano = err?.response?.data?.message || err?.response?.data || err?.message || 'Error en backend';
          if (err?.response?.data) {
            console.error('Respuesta completa de error back:', err.response.data);
            if (err.response.data.errors) {
              const errores = err.response.data.errors;
              let detalle = '';
              for (let campo in errores) {
                if (Array.isArray(errores[campo])) {
                  const nombreCampo = campo === 'Nombre' ? 'nombre' : campo;
                  detalle += `${nombreCampo}: ${errores[campo].join('; ')}\n`;
                }
              }
              enqueueSnackbar('❌ Error en los datos: ' + detalle, { variant: 'error', autoHideDuration: 15000 });
              fallos++;
              continue;
            }
          }
          // Agregar impresión de error en consola para depuración completa:
          if (err?.response?.data) {
            console.error('Respuesta completa de error back:', err.response.data);
          }
          if (typeof mensajePlano !== 'string') {
            try {
              mensajePlano = JSON.stringify(mensajePlano);
            } catch(e) {
              mensajePlano = '[object Object]';
            }
          }
          // Busca si mensaje es un JSON RFC-9110 u OpenAPI con campos title/detail
          let mostrado = false;
          if (typeof mensajePlano === 'string') {
            // Mensaje de duplicado
            if (
              mensajePlano.includes('PRIMARY KEY') ||
              mensajePlano.toLowerCase().includes('duplicate') ||
              mensajePlano.includes('Violation of PRIMARY KEY constraint')
            ) {
              enqueueSnackbar(`🚩 El cliente con código ${cliente.nroCliente} ya está cargado.`, { variant: 'error', autoHideDuration: 12000 });
              mostrado = true;
            } else {
              // Intenta parsear como JSON OpenAPI para mostrar title/detail
              try {
                const jsonObj = JSON.parse(mensajePlano);
                // Si el mensaje incluye title y detail, dalo en español
                if(jsonObj && (jsonObj.title || jsonObj.detail)) {
                  enqueueSnackbar(`❌ Error en los datos: ${jsonObj.title ? jsonObj.title + '. ' : ''}${jsonObj.detail ? jsonObj.detail : ''}`, { variant: 'error', autoHideDuration: 12000 });
                  mostrado = true;
                }
              } catch {}
            }
          }
          // Si no lo mostró, como fallback muestra mensaje crudo truncado
          if (!mostrado) {
            enqueueSnackbar(`❌ Fila ${i+2}: Error al guardar: ${String(mensajePlano).slice(0,120)}`, { variant: 'error', autoHideDuration: 8000 });
          }
          fallos++;
        }
      }
      enqueueSnackbar(`Importación completada. Éxitos: ${exitos}, Fallos: ${fallos}`, { variant: fallos > 0 ? 'error' : 'success', autoHideDuration: 4500 });
      fetchClientes(page, rowsPerPage, searchTerm);
      setTimeout(() => { setOpenImportar(false); }, 800);
    } catch (error) {
      enqueueSnackbar('Error leyendo el archivo Excel (¿archivo corrupto/formato incorrecto?)', { variant: 'error', autoHideDuration: 9000 });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Clientes
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/ventas/clientes/nuevo')}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25,118,210,0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25,118,210,0.4)',
                  }
                }}
              >
                {isMobile ? 'Nuevo' : 'Nuevo Cliente'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<AttachFileIcon />}
                onClick={() => setOpenImportar(true)}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                }}
              >
                Importar clientes
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre, CUIT o N° de cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {clientesData.items.map((cliente) => {
                const tipoIvaChip = getTipoIvaChip(cliente.tipoIva);
                return (
                  <Card
                    key={cliente.nroCliente}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {cliente.nombre}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              N° Cliente: {cliente.nroCliente}
                            </Typography>
                          </Box>
                          <Chip
                            label={tipoIvaChip.label}
                            size="small"
                            sx={{
                              bgcolor: tipoIvaChip.bgColor,
                              color: tipoIvaChip.textColor,
                              fontWeight: 500,
                              borderRadius: 1.5,
                            }}
                          />
                        </Stack>
                        {cliente.razonSocial && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Razón Social:</strong> {cliente.razonSocial}
                          </Typography>
                        )}
                        {cliente.cuit && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CUIT:</strong> {cliente.cuit}
                          </Typography>
                        )}
                        {cliente.direccion && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Dirección:</strong> {cliente.direccion}
                          </Typography>
                        )}
                        {cliente.telefono1 && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Teléfono:</strong> {cliente.telefono1}
                          </Typography>
                        )}
                        {cliente.email && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Email:</strong> {cliente.email}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Tooltip title="Editar cliente">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/ventas/clientes/${encodeURIComponent(cliente.nroCliente)}`)}
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1976d2' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar cliente">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(cliente.nroCliente)}
                              sx={{
                                bgcolor: '#f44336',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#d32f2f' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {clientesData.items.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No hay clientes para mostrar
                </Typography>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={clientesData.totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
              />
            </Stack>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>N° Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Razón Social</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>CUIT</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Dirección</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Teléfono</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo IVA</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientesData.items.map((cliente) => {
                    const tipoIvaChip = getTipoIvaChip(cliente.tipoIva);
                    return (
                      <TableRow
                        key={cliente.nroCliente}
                        sx={{
                          '&:hover': {
                            bgcolor: '#f9f9f9',
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{cliente.nroCliente}</TableCell>
                        <TableCell>{cliente.nombre}</TableCell>
                        <TableCell>{cliente.razonSocial || '-'}</TableCell>
                        <TableCell>{cliente.cuit || '-'}</TableCell>
                        <TableCell>{cliente.direccion || '-'}</TableCell>
                        <TableCell>{cliente.telefono1 || '-'}</TableCell>
                        <TableCell>{cliente.email || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={tipoIvaChip.label}
                            size="small"
                            sx={{
                              bgcolor: tipoIvaChip.bgColor,
                              color: tipoIvaChip.textColor,
                              fontWeight: 500,
                              borderRadius: 1.5,
                              minWidth: 100,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Editar cliente">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/ventas/clientes/${encodeURIComponent(cliente.nroCliente)}`)}
                                sx={{
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': {
                                    bgcolor: '#1976d2',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar cliente">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(cliente.nroCliente)}
                                sx={{
                                  bgcolor: '#f44336',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': {
                                    bgcolor: '#d32f2f',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {clientesData.items.length === 0 && (
                <Box sx={{ py: 4 }}>
                  <Typography align="center" color="text.secondary">
                    No hay clientes para mostrar
                  </Typography>
                </Box>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={clientesData.totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
      {/* Modal de importación */}
      <Dialog open={openImportar} onClose={() => setOpenImportar(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap:1 }}>
            <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 30, mr: 1 }} />
            <Typography variant="h6" fontWeight={700}>Importar Clientes</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            {/* Bloque 1: Descargar plantilla */}
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, mb: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudDownloadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>1. Descargar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>Descarga la plantilla Excel con el formato correcto para importar clientes.</Typography>
              <Button variant="contained" color="info" fullWidth sx={{ fontWeight: 700, py: 1.2, mb: 1 }} startIcon={<CloudDownloadIcon />} onClick={handleDescargarPlantilla}>
                Descargar Plantilla
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            {/* Bloque 2: Importar desde archivo */}
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudUploadIcon color="success" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>2. Importar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Selecciona tu archivo Excel completado para importar los clientes.
              </Typography>
              <label htmlFor="excel-upload-clientes">
                <input
                  id="excel-upload-clientes"
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={onImportChangeClientes}
                />
                <Button variant="contained" fullWidth color="success" startIcon={<CloudUploadIcon />} component="span" sx={{ fontWeight: 700, py: 1.2 }}>
                  Seleccionar Archivo
                </Button>
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImportar(false)} color="inherit">Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Clientes;
